import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { Loader2, Tag, X } from "lucide-react";
import { toast } from "sonner";

type ModuloEtiqueta = "pedido" | "produto" | "cliente" | "orcamento";

type Etiqueta = {
  id: number;
  nome: string;
  cor: string;
  ativo?: boolean;
};

function dataHoraBrasileira(valor: string | Date) {
  return new Date(valor).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

export function EtiquetasChips({ etiquetas, vazio = "-" }: { etiquetas?: Etiqueta[]; vazio?: string }) {
  if (!etiquetas?.length) return <span className="text-muted-foreground">{vazio}</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {etiquetas.map((etiqueta) => (
        <span
          key={etiqueta.id}
          className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${etiqueta.ativo === false ? "opacity-50 line-through" : ""}`}
          style={{ backgroundColor: etiqueta.cor, color: "#ffffff" }}
          title={etiqueta.ativo === false ? `${etiqueta.nome} (inativa)` : etiqueta.nome}
        >
          {etiqueta.nome}
        </span>
      ))}
    </div>
  );
}

export function EtiquetasEditor({
  modulo,
  entidadeId,
  disabled = false,
  onChanged,
}: {
  modulo: ModuloEtiqueta;
  entidadeId: number;
  disabled?: boolean;
  onChanged?: () => void;
}) {
  const utils = trpc.useUtils();
  const { data: atuais = [], isLoading: carregandoAtuais } = trpc.etiquetas.porEntidades.useQuery(
    { modulo, entidadeIds: [entidadeId] },
    { enabled: entidadeId > 0 }
  );
  const { data: disponiveis = [], isLoading: carregandoDisponiveis } = trpc.etiquetas.disponiveis.useQuery(
    { modulo, entidadeId },
    { enabled: entidadeId > 0 }
  );
  const { data: historico = [], isLoading: carregandoHistorico } = trpc.etiquetas.historico.useQuery(
    { modulo, entidadeId },
    { enabled: entidadeId > 0 }
  );

  const invalidar = async () => {
    await Promise.all([
      utils.etiquetas.porEntidades.invalidate(),
      utils.etiquetas.disponiveis.invalidate({ modulo, entidadeId }),
      utils.etiquetas.historico.invalidate({ modulo, entidadeId }),
    ]);
    onChanged?.();
  };

  const adicionar = trpc.etiquetas.adicionar.useMutation({
    onSuccess: async () => {
      await invalidar();
      toast.success("Etiqueta adicionada.");
    },
    onError: (error) => toast.error(error.message),
  });

  const remover = trpc.etiquetas.remover.useMutation({
    onSuccess: async () => {
      await invalidar();
      toast.success("Etiqueta removida.");
    },
    onError: (error) => toast.error(error.message),
  });

  const etiquetasAtuais: Etiqueta[] = atuais[entidadeId] || [];
  const carregando = carregandoAtuais || carregandoDisponiveis || carregandoHistorico;

  return (
    <section className="space-y-3 rounded-md border p-3">
      <div className="flex items-center gap-2">
        <Tag className="h-4 w-4" />
        <Label className="font-semibold">Etiquetas</Label>
      </div>

      {carregando ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando etiquetas...</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {disponiveis.map((etiqueta: Etiqueta) => {
              const aplicada = etiquetasAtuais.some((atual) => atual.id === etiqueta.id);
              return (
                <label key={etiqueta.id} className={`flex cursor-pointer items-center gap-2 rounded border px-2 py-1 text-sm ${disabled ? "cursor-not-allowed opacity-60" : ""}`}>
                  <Checkbox
                    checked={aplicada}
                    disabled={disabled || adicionar.isPending || remover.isPending}
                    onCheckedChange={(marcada) => {
                      if (marcada) adicionar.mutate({ etiquetaId: etiqueta.id, modulo, entidadeId });
                      else remover.mutate({ etiquetaId: etiqueta.id, modulo, entidadeId });
                    }}
                  />
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: etiqueta.cor }} />
                  {etiqueta.nome}
                </label>
              );
            })}
            {disponiveis.length === 0 && <span className="text-sm text-muted-foreground">Nenhuma etiqueta ativa disponível para este módulo.</span>}
          </div>

          {etiquetasAtuais.some((etiqueta) => etiqueta.ativo === false) && (
            <div className="space-y-1">
              <span className="text-sm text-muted-foreground">Etiquetas inativas já vinculadas:</span>
              <div className="flex flex-wrap gap-1">
                {etiquetasAtuais.filter((etiqueta) => etiqueta.ativo === false).map((etiqueta) => (
                  <Button
                    key={etiqueta.id}
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disabled || remover.isPending}
                    onClick={() => remover.mutate({ etiquetaId: etiqueta.id, modulo, entidadeId })}
                    className="h-7 gap-1"
                  >
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: etiqueta.cor }} />
                    {etiqueta.nome}
                    <X className="h-3 w-3" />
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="border-t pt-3">
            <p className="mb-2 text-sm font-medium">Histórico de etiquetas</p>
            {historico.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma alteração de etiqueta registrada.</p>
            ) : (
              <div className="max-h-36 space-y-1 overflow-y-auto text-sm">
                {historico.map((evento: any) => (
                  <p key={evento.id}>
                    <span className="font-medium" style={{ color: evento.etiquetaCor }}>{evento.etiquetaNome}</span>{" "}
                    foi {evento.acao} por {evento.userName || "usuário não identificado"} em {dataHoraBrasileira(evento.criadoEm)}.
                  </p>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
